// Route table (ADR-017) — replaces Next's file-system routing.
//
// Paths are unchanged from the App Router layout they came from, so every
// bookmark, redirect target, and GitHub App setup URL keeps working. The file
// paths under `app/` still carry Next's naming (`(app)/`, `[slug]/`,
// `page.tsx`); renaming 23 files was left out of this migration to keep the
// diff mechanical, and is a follow-up.
//
// Route components are imported eagerly: one bundle, no code splitting. That is
// the trade-off ADR-017 records under "Negative". Splitting the heavy routes
// (Playground, agent detail) is a follow-up, not a migration concern.

import { createBrowserRouter, Navigate, useLocation } from "react-router-dom";
import type { RouteObject } from "react-router-dom";

import AppLayout from "@/app/(app)/layout";
import AgentDetailPage from "@/app/(app)/agents/[slug]/page";
import AgentVersionPage from "@/app/(app)/agents/[slug]/versions/[label]/page";
import AgentsPage from "@/app/(app)/agents/page";
import CapabilitiesPage from "@/app/(app)/capabilities/page";
import DashboardPage from "@/app/(app)/dashboard/page";
import DomainsPage from "@/app/(app)/domains/page";
import EmbeddingModelsPage from "@/app/(app)/embedding-models/page";
import GithubPage from "@/app/(app)/github/page";
import GovernanceActivityPage from "@/app/(app)/governance/activity/page";
import GovernancePage from "@/app/(app)/governance/page";
import AuditLogsPage from "@/app/(app)/audit-logs/page";
import EvaluationAgentPage from "@/app/(app)/evaluations/[slug]/page";
import EvaluationsPage from "@/app/(app)/evaluations/page";
import GovernanceReviewPage from "@/app/(app)/governance/[reviewId]/page";
import SecurityFindingsPage from "@/app/(app)/governance/security/page";
import KnowledgeBaseDetailPage from "@/app/(app)/knowledge-bases/[slug]/page";
import KnowledgeBasesPage from "@/app/(app)/knowledge-bases/page";
import LlmModelsPage from "@/app/(app)/llm-models/page";
import ModelCatalogPage from "@/app/(app)/model-catalog/page";
import PlaygroundPage from "@/app/(app)/playground/page";
import TenantDetailPage from "@/app/(app)/tenants/[slug]/page";
import TenantNewPage from "@/app/(app)/tenants/new/page";
import TenantsPage from "@/app/(app)/tenants/page";
import TrustedSystemsPage from "@/app/(app)/trusted-systems/page";
import UsersPage from "@/app/(app)/users/page";
import ConversationsPage from "@/app/(app)/conversations/page";
import ObservabilityPage from "@/app/(app)/observability/page";
import ConversationPage from "@/app/chat/[id]/page";
import ChatAgentsPage from "@/app/chat/agents/page";
import ChatLayout from "@/app/chat/layout";
import ChatHomePage from "@/app/chat/page";
import VectorStoresPage from "@/app/(app)/vector-stores/page";
import ChangePasswordPage from "@/app/change-password/page";
import OrganizationLoginPage from "@/app/login/[slug]/page";
import LoginPage from "@/app/login/page";
import SelectOrganizationPage from "@/app/sso/select-organization/page";

/**
 * Redirect that carries the query string across.
 *
 * `/settings/github` is not a cosmetic redirect: a registered GitHub App's
 * setup URL is baked into the App itself, so an install started against an
 * older deployment still lands there, carrying `installation_id` and the signed
 * `state` that bind the installation. Drop the query and the connection
 * silently fails.
 *
 * ADR-017 puts this on a CloudFront Function in production. It is kept here as
 * well so it works in local dev, where there is no CloudFront in front.
 *
 * Exported for `routes.test.tsx` — the failure mode here is silent (the install
 * completes, the binding does not), so it is covered by a test rather than by
 * anyone noticing.
 */
export function RedirectWithQuery({ to }: { to: string }) {
  const { search } = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
}

/** The route table. Exported separately from `router` so tests can mount it in
 *  a memory router instead of driving a real browser history. */
export const routes: RouteObject[] = [
  { path: "/", element: <Navigate to="/dashboard" replace /> },
  { path: "/login", element: <LoginPage /> },
  // An organisation's own login link: adds "Login with Google" (its own client).
  { path: "/login/:slug", element: <OrganizationLoginPage /> },
  { path: "/sso/select-organization", element: <SelectOrganizationPage /> },
  { path: "/change-password", element: <ChangePasswordPage /> },
  { path: "/settings/github", element: <RedirectWithQuery to="/github" /> },

  // Everything below is behind the session gate in AppLayout, which resolves
  // /me and bounces on failure or a pending forced password change.
  {
    element: <AppLayout />,
    children: [
      { path: "/dashboard", element: <DashboardPage /> },

      { path: "/tenants", element: <TenantsPage /> },
      { path: "/tenants/new", element: <TenantNewPage /> },
      { path: "/tenants/:slug", element: <TenantDetailPage /> },
      { path: "/model-catalog", element: <ModelCatalogPage /> },
      { path: "/trusted-systems", element: <TrustedSystemsPage /> },
      { path: "/users", element: <UsersPage /> },
      { path: "/conversations", element: <ConversationsPage /> },
      { path: "/observability", element: <ObservabilityPage /> },

      { path: "/agents", element: <AgentsPage /> },
      { path: "/agents/:slug", element: <AgentDetailPage /> },
      { path: "/agents/:slug/versions/:label", element: <AgentVersionPage /> },

      { path: "/capabilities", element: <CapabilitiesPage /> },
      { path: "/domains", element: <DomainsPage /> },
      { path: "/knowledge-bases", element: <KnowledgeBasesPage /> },
      { path: "/knowledge-bases/:slug", element: <KnowledgeBaseDetailPage /> },
      { path: "/llm-models", element: <LlmModelsPage /> },
      { path: "/embedding-models", element: <EmbeddingModelsPage /> },
      { path: "/vector-stores", element: <VectorStoresPage /> },
      { path: "/audit-logs", element: <AuditLogsPage /> },
      { path: "/evaluations", element: <EvaluationsPage /> },
      { path: "/evaluations/:slug", element: <EvaluationAgentPage /> },

      { path: "/governance", element: <GovernancePage /> },
      { path: "/governance/activity", element: <GovernanceActivityPage /> },
      { path: "/governance/security", element: <SecurityFindingsPage /> },
      { path: "/governance/:reviewId", element: <GovernanceReviewPage /> },

      { path: "/github", element: <GithubPage /> },
      { path: "/playground", element: <PlaygroundPage /> },
    ],
  },

  // The chat app — its own shell and session gate (users land here; admins and
  // engineers reach it from the console).
  {
    element: <ChatLayout />,
    children: [
      { path: "/chat", element: <ChatHomePage /> },
      { path: "/chat/agents", element: <ChatAgentsPage /> },
      { path: "/chat/:id", element: <ConversationPage /> },
    ],
  },

  // Unknown path → dashboard, matching the App Router's behaviour of falling
  // back to the app shell rather than showing a bare 404.
  { path: "*", element: <Navigate to="/dashboard" replace /> },
];

export const router = createBrowserRouter(routes);
